import { useMutation } from "@apollo/client";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { FormEvent, KeyboardEvent, useEffect, useState } from "react";
import { useBeforeunload } from "react-beforeunload";
import { Prompt } from "react-router";
import {
  FeedbackState,
  GetArtifactSidebarQueryQuery,
  UpdateArtifactTitleMutationMutation,
  UpdateArtifactTitleMutationMutationVariables,
} from "types/graphql-schema";

import Button from "@components/button/button";
import GraphqlError from "@components/error/graphql-error";
import Form from "@components/form/form";
import { classNames } from "@helpers/css";
import {
  isCommandEnterEvent,
  isEnterEvent,
  isEscapeEvent,
} from "@helpers/helpers";

import getArtifactActivitiesQuery from "../graphql/get-artifact-activities-query";
import updateArtifactTitleMutation from "../graphql/update-artifact-title-mutation";

const ArtifactSidebarHeader = ({
  artifact,
  className = "",
  variant = "sidebar",
}: {
  artifact: GetArtifactSidebarQueryQuery["artifact"];
  className?: string;
  // "page" renders the editable title as plain bold page text, with none of the
  // input-like border/hover/fill the sidebar uses.
  variant?: "sidebar" | "page";
}) => {
  const isPage = variant === "page";
  const titleClassName = classNames(
    "text-2xl py-1 flex-1 tracking-tight w-0 break-words",
    isPage
      ? // `!text-gray-900` overrides the global `.ProseMirror { text-gray-600 }`
        // rule; `js-artifact-page-title` scopes the outline reset in index.css.
        "font-bold !text-gray-900 js-artifact-page-title"
      : "font-medium px-2 -ml-2 rounded border border-white",
    className,
  );
  const isDecision = artifact.__typename === "DecisionArtifactNode";
  const isRating = artifact.__typename === "RatingArtifactNode";
  const isFeedback = artifact.__typename === "FeedbackArtifactNode";
  const artifactTitle =
    (isDecision
      ? artifact.decision
      : isRating
        ? artifact.rating?.title
        : artifact.title) || "";
  const [title, setTitle] = useState(artifactTitle);
  const canEdit =
    isFeedback && artifact.feedbackState === FeedbackState.Requested
      ? false
      : artifact.canUpdate.permission && !isRating;

  const trimmedTitle = title.trim();

  const [saveArtifact, { error, loading }] = useMutation<
    UpdateArtifactTitleMutationMutation,
    UpdateArtifactTitleMutationMutationVariables
  >(updateArtifactTitleMutation);

  const handleSaveTitle = (e: FormEvent) => {
    e.preventDefault();
    if (trimmedTitle && artifactTitle !== trimmedTitle) {
      saveArtifact({
        variables: {
          artifactId: artifact.id,
          ...(isDecision && {
            additionalFields: { decision: trimmedTitle },
          }),
          ...(!isDecision && { title: trimmedTitle }),
        },
        optimisticResponse: {
          createOrUpdateArtifact: {
            artifact: {
              id: artifact.id,
              __typename: artifact.__typename,
              decision: trimmedTitle,
              title: trimmedTitle,
            },
            __typename: "CreateOrUpdateArtifactMutation",
          },
          __typename: "Mutation",
        },
        refetchQueries: [getArtifactActivitiesQuery],
      });
    }
  };

  const handleKeyDownTitle = (e: KeyboardEvent) => {
    if (isCommandEnterEvent(e) || isEnterEvent(e)) {
      handleSaveTitle(e);
    }
    if (isEscapeEvent(e)) {
      handleCancelTitle();
    }
  };

  const handleChangeTitle = (title: string) => {
    setTitle(title);
  };

  const TitleDocument = Document.extend({
    name: "artifact-title-document",
    content: "block",
  });

  const editor = useEditor({
    editable: true,
    autofocus: false,
    injectNonce: window.TF?.cspNonce,
    extensions: [
      Text,
      Paragraph,
      TitleDocument,
      Placeholder.configure({ placeholder: "Title..." }),
    ],
    editorProps: {
      attributes: {
        class: classNames(
          titleClassName,
          "w-full block resize-none",
          isPage && "focus:outline-none",
          !isPage &&
            "hover:bg-gray-100 hover:border-gray-200 focus:border-gray-100",
        ),
        "aria-label": "Textbox topic title",
      },
    },
    content: title,
    onUpdate: ({ editor }) => {
      handleChangeTitle(editor.getText());
    },
  });

  const handleCancelTitle = () => {
    setTitle(artifactTitle);
    editor?.chain().setContent(artifactTitle).run();
  };

  useBeforeunload((event) => {
    if (canEdit && artifactTitle !== title) {
      event.preventDefault();
    }
  });

  // when artifact changes we hide form
  // and reset form values to current artifact
  useEffect(() => {
    if (artifact.canRead.permission) {
      setTitle(artifactTitle);
      if (
        artifact.__typename !== "FeedbackArtifactNode" &&
        artifact.__typename !== "RecognitionArtifactNode"
      ) {
        editor?.chain().setContent(artifactTitle).focus("end").run();
      }
    }
  }, [artifact.canRead.permission, artifactTitle, editor]);

  const handleBlurTitle = () => {
    if (title.trim() === "") {
      handleCancelTitle();
    }
  };

  // Render
  return (
    <div data-testid="topic-sidebar-header" className="flex-1">
      <div className="flex gap-2">
        {canEdit ? (
          <Form
            name="title-form"
            onSubmit={handleSaveTitle}
            data-lpignore="true"
            autoComplete="off"
            aria-label="Artifact title form"
            className="flex-1"
          >
            <Prompt
              when={title !== artifactTitle}
              message="Leave page? Changes you made may not be saved."
            />
            <EditorContent
              editor={editor}
              className="inline"
              onKeyDown={handleKeyDownTitle}
              onBlur={handleBlurTitle}
            />
            {artifactTitle !== title && (
              <div className="flex gap-4 mt-2">
                <Button
                  type="submit"
                  text="Save"
                  theme="primary"
                  disabled={loading || trimmedTitle.length === 0}
                />
                <Button
                  onClick={handleCancelTitle}
                  text="Cancel"
                  disabled={loading}
                />
              </div>
            )}
          </Form>
        ) : (
          <h2 className={titleClassName} aria-label="Sidebar artifact title">
            {artifactTitle}
          </h2>
        )}
      </div>

      {error && (
        <div className="mt-3">
          <GraphqlError
            error={error}
            whatDidNotWork="The title could not be saved."
          />
        </div>
      )}
    </div>
  );
};

export default ArtifactSidebarHeader;
